<div data-spartan-page class="p-6 md:p-8 space-y-5"
     style="background: rgb(var(--ds-bg, 250 250 249)); min-height: 100%;">

  <!-- Page header -->
  <app-page-header
    eyebrow="Forwarding"
    title="Inquiries"
    subtitle="Pre-quotation requests across ATR and H&T."
  >
    <div actions class="flex items-center gap-2">
      <button hlmBtn variant="outline" class="gap-2 h-9" (click)="exportToExcel()">
        <ng-icon hlm name="lucideDownload" class="size-4" />
        Export
      </button>
      <a routerLink="new">
        <button hlmBtn class="gap-2 h-9">
          <ng-icon hlm name="lucidePlus" class="size-4" />
          New Inquiry
        </button>
      </a>
    </div>
  </app-page-header>

  <!-- Filter / table card -->
  <div class="rounded-lg border border-border bg-card shadow-sm">

    <!-- Toolbar -->
    <div class="px-4 py-3 border-b border-border flex flex-wrap items-center gap-2">

      <!-- Search with leading icon -->
      <div class="relative flex-1 min-w-[220px] max-w-sm">
        <ng-icon hlm name="lucideSearch"
          class="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
        <input
          class="h-9 w-full rounded-md border border-border-strong bg-surface text-sm pl-8 pr-8 placeholder:text-stone-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:border-accent transition"
          type="search"
          placeholder="Search inquiry #, client..."
          [(ngModel)]="searchTerm"
          (input)="filterInquiries()"
          aria-label="Search inquiries" />
        @if (searchTerm) {
          <button
            class="appearance-none border-0 bg-transparent absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded"
            (click)="searchTerm = ''; filterInquiries()"
            aria-label="Clear search">
            <ng-icon hlm name="lucideX" class="size-3.5" />
          </button>
        }
      </div>

      <!-- Status filter — canonical select wrapper -->
      <div class="relative">
        <select
          class="appearance-none h-9 w-36 rounded-md border border-border-strong bg-surface text-sm pl-3 pr-8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent transition"
          [(ngModel)]="selectedStatus"
          (change)="filterInquiries()"
          aria-label="Filter by status">
          <option value="">All Status</option>
          <option value="New">New</option>
          <option value="In Progress">In Progress</option>
          <option value="Quoted">Quoted</option>
          <option value="Proceed">Proceed</option>
          <option value="Rejected">Rejected</option>
        </select>
        <ng-icon hlm name="lucideChevronDown"
          class="absolute right-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
      </div>

      <!-- Company filter — canonical select wrapper -->
      <div class="relative">
        <select
          class="appearance-none h-9 w-32 rounded-md border border-border-strong bg-surface text-sm pl-3 pr-8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent transition"
          [(ngModel)]="selectedCompany"
          (change)="filterInquiries()"
          aria-label="Filter by company">
          <option value="">All Companies</option>
          <option value="HT">H&T (HT)</option>
          <option value="ATR">ATR</option>
        </select>
        <ng-icon hlm name="lucideChevronDown"
          class="absolute right-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
      </div>

      <!-- Client Type filter — canonical select wrapper -->
      <div class="relative">
        <select
          class="appearance-none h-9 w-36 rounded-md border border-border-strong bg-surface text-sm pl-3 pr-8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent transition"
          [(ngModel)]="selectedClientType"
          (change)="filterInquiries()"
          aria-label="Filter by client type">
          <option value="">All Types</option>
          <option value="In-House">In-House</option>
          <option value="Agent">Agent (Freehand)</option>
          <option value="Sales">Sales</option>
          <option value="New">New</option>
        </select>
        <ng-icon hlm name="lucideChevronDown"
          class="absolute right-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
      </div>

      <!-- Date from -->
      <input
        class="h-9 w-36 rounded-md border border-border-strong bg-surface text-sm px-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent transition"
        type="date"
        [(ngModel)]="dateFrom"
        (change)="filterInquiries()"
        aria-label="Filter from date" />

      <!-- Clear filters -->
      @if (searchTerm || selectedStatus || selectedCompany || selectedClientType || dateFrom) {
        <button
          hlmBtn variant="ghost" size="sm"
          class="h-9 text-muted-foreground gap-1.5"
          (click)="resetFilters()">
          <ng-icon hlm name="lucideX" class="size-3.5" />
          Clear
        </button>
      }

      <div class="flex-1"></div>

      <span class="text-xs text-muted-foreground">{{ filteredInquiries.length }} result{{ filteredInquiries.length === 1 ? '' : 's' }}</span>
    </div>

    <!-- Table -->
    @if (filteredInquiries.length === 0) {
      <div class="py-16 flex flex-col items-center text-center">
        <ng-icon hlm name="lucideInbox" class="size-10 text-muted-foreground mb-3" />
        <h3 class="text-base font-semibold">No inquiries found</h3>
        <p class="text-sm text-muted-foreground max-w-sm mt-1">
          Try adjusting your filters or
          <a routerLink="new" class="text-accent hover:underline">create your first inquiry</a>.
        </p>
      </div>
    } @else {
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-[11px] font-medium uppercase tracking-wider text-muted-foreground border-b border-border-strong">
              <th class="px-4 py-2.5 text-left font-medium">Inquiry #</th>
              <th class="px-4 py-2.5 text-left font-medium">Date</th>
              <th class="px-4 py-2.5 text-left font-medium">Company</th>
              <th class="px-4 py-2.5 text-left font-medium">Client</th>
              <th class="px-4 py-2.5 text-left font-medium">Type</th>
              <th class="px-4 py-2.5 text-left font-medium">Services</th>
              <th class="px-4 py-2.5 text-left font-medium">Route</th>
              <th class="px-4 py-2.5 text-left font-medium">Mode</th>
              <th class="px-4 py-2.5 text-left font-medium">Status</th>
              <th class="w-28"></th>
            </tr>
          </thead>
          <tbody>
            @for (inquiry of filteredInquiries; track inquiry.id) {
              <tr class="border-b border-border last:border-0 even:bg-surface-raised/50 hover:bg-surface-raised transition-colors cursor-pointer group"
                  [routerLink]="[inquiry.id]">

                <!-- Inquiry # -->
                <td class="px-4 h-12">
                  <span class="font-mono text-[12.5px] font-medium text-accent">{{ inquiry.inquiryNumber }}</span>
                </td>

                <!-- Date -->
                <td class="px-4 h-12">
                  <span class="tabular-nums text-xs text-muted-foreground whitespace-nowrap">{{ inquiry.createdDate | date:'MM/dd/yyyy' }}</span>
                </td>

                <!-- Company badge -->
                <td class="px-4 h-12">
                  <span hlmBadge
                    [variant]="inquiry.company === 'HT' ? 'default' : 'secondary'"
                    class="rounded-md h-5 text-[11px] font-mono px-2">
                    {{ inquiry.company }}
                  </span>
                </td>

                <!-- Client name -->
                <td class="px-4 h-12">
                  <span class="font-medium truncate block max-w-[200px]">{{ inquiry.clientName }}</span>
                </td>

                <!-- Client type -->
                <td class="px-4 h-12 text-xs text-muted-foreground">{{ inquiry.clientType }}</td>

                <!-- Services chips -->
                <td class="px-4 h-12">
                  <div class="flex flex-wrap gap-1">
                    @for (service of inquiry.services; track service) {
                      <span hlmBadge variant="secondary" class="rounded-md h-5 text-[11px] px-1.5">{{ service }}</span>
                    }
                  </div>
                </td>

                <!-- Route -->
                <td class="px-4 h-12">
                  <span class="font-mono text-xs">{{ inquiry.origin }} → {{ inquiry.destination }}</span>
                </td>

                <!-- Mode badge -->
                <td class="px-4 h-12">
                  <span hlmBadge variant="outline" class="rounded-md h-5 text-[11px] px-2">{{ inquiry.mode }}</span>
                </td>

                <!-- Status -->
                <td class="px-4 h-12">
                  <app-status-badge [variant]="getStatusVariant(inquiry.status)" [label]="inquiry.status" />
                </td>

                <!-- Row actions -->
                <td class="px-4 h-12 text-right">
                  <div class="flex items-center justify-end gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <a [routerLink]="[inquiry.id]" (click)="$event.stopPropagation()">
                      <button hlmBtn variant="ghost" size="sm" class="h-7 w-7 p-0" title="View" aria-label="View inquiry">
                        <ng-icon hlm name="lucideEye" class="size-3.5" />
                      </button>
                    </a>
                    @if (inquiry.status === 'New' || inquiry.status === 'In Progress') {
                      <button hlmBtn variant="outline" size="sm" class="h-7 gap-1 px-2 text-xs text-accent border-accent/40 hover:bg-accent-light"
                        (click)="createQuote(inquiry); $event.stopPropagation()"
                        title="Create quote" aria-label="Create quote for inquiry">
                        <ng-icon hlm name="lucideFilePlus" class="size-3.5" />
                        Quote
                      </button>
                    }
                    <button hlmBtn variant="ghost" size="sm"
                      class="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                      (click)="deleteInquiry(inquiry); $event.stopPropagation()"
                      title="Delete" aria-label="Delete inquiry">
                      <ng-icon hlm name="lucideTrash2" class="size-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <!-- Pagination footer -->
      <div class="px-4 py-3 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
        <span>Showing <span class="font-medium text-foreground">{{ filteredInquiries.length }}</span> of <span class="font-medium text-foreground">{{ inquiries.length }}</span> inquiries</span>
      </div>
    }
  </div>

</div>
